import type { RouteRecordRaw } from 'vue-router';
import RouteLayout from '../RouteLayout.vue';
import {
  DIARY_DAYS_COLLECTION,
  RECIPES_COLLECTION,
  USER_FOODS_COLLECTION,
  GOALS_HISTORY_COLLECTION,
  WEIGHT_HISTORY_COLLECTION,
  SETTINGS_COLLECTION,
  MEALS_COLLECTION,
} from '@macro-tracker/models';

const DEVELOPER_DETAIL_ROUTE_BY_NAMESPACE: Record<string, string> = {
  [DIARY_DAYS_COLLECTION]: "settings-developer-data-detail-diary-entries",
  [USER_FOODS_COLLECTION]: "settings-developer-data-detail-food-items",
  [RECIPES_COLLECTION]: "settings-developer-data-detail-recipes",
  [GOALS_HISTORY_COLLECTION]: "settings-developer-data-detail-goals-history",
  [WEIGHT_HISTORY_COLLECTION]: "settings-developer-data-detail-weight-history",
  [SETTINGS_COLLECTION]: "settings-developer-data-detail-settings",
  [MEALS_COLLECTION]: "settings-developer-data-detail-meals",
  "macrotracker:diary-days": "settings-developer-data-detail-diary-entries",
  "macrotracker:diary-entries": "settings-developer-data-detail-diary-entries",
  "macrotracker:food-items": "settings-developer-data-detail-food-items",
  "macrotracker:recipes": "settings-developer-data-detail-recipes",
  "macrotracker:goals-history": "settings-developer-data-detail-goals-history",
  "macrotracker:weight-history": "settings-developer-data-detail-weight-history",
  "macrotracker:settings": "settings-developer-data-detail-settings",
  "macrotracker:meals": "settings-developer-data-detail-meals",
  "macrotracker:foods-search-docs": "settings-developer-data-detail-foods-search-docs",
  "macrotracker:sync-configuration": "settings-developer-data-detail-sync-configuration",
  "macrotracker:auth-session": "settings-developer-data-detail-auth-session",
  "sync-records": "settings-developer-data-detail-sync-records",
  "macrotracker:daily-nutrition-totals": "settings-developer-data-detail-daily-nutrition-totals",
};

const resolveDeveloperDetailRouteName = (namespace: unknown): string | null => {
  if (typeof namespace !== "string") {
    return null;
  }
  return DEVELOPER_DETAIL_ROUTE_BY_NAMESPACE[namespace] ?? null;
};

export const settingsRoutes: RouteRecordRaw = {
  path: 'settings',
  component: RouteLayout,
  children: [
    {
      path: '',
      name: 'tabs-settings',
      component: () => import('@/views/SettingsPage.vue'),
    },
    {
      path: 'meals',
      name: 'settings-meals',
      component: () => import('@/views/MealsSettingsPage.vue'),
    },
    {
      path: 'share-groups',
      name: 'settings-share-groups',
      component: () => import('@/views/ShareGroupsPage.vue'),
    },
    {
      path: 'coach-sharing',
      name: 'settings-coach-sharing',
      component: () => import('@/views/CoachSharingPage.vue'),
    },
    {
      path: 'share-groups/:id',
      name: 'settings-share-group-detail',
      component: () => import('@/views/ShareGroupDetailPage.vue'),
    },
    {
      path: 'sync',
      name: 'settings-sync',
      component: () => import('@/views/SyncSettingsPage.vue'),
    },
    {
      path: 'privacy-policy',
      name: 'settings-privacy-policy',
      component: () => import('@/views/PrivacyPolicyPage.vue'),
    },
    {
      path: 'import/chronometer',
      name: 'settings-import-chronometer',
      component: () => import('@/views/ChronometerImportPage.vue'),
    },
    {
      path: 'developer-data/diary-days/:id',
      alias: 'developer-data/diary-entries/:id',
      name: 'settings-developer-data-detail-diary-entries',
      component: () => import('@/views/developer-data/DeveloperDiaryEntriesDetailPage.vue'),
      meta: { developer: true },
    },
    {
      path: 'developer-data/food-items/:id',
      name: 'settings-developer-data-detail-food-items',
      component: () => import('@/views/developer-data/DeveloperFoodItemsDetailPage.vue'),
      meta: { developer: true },
    },
    {
      path: 'developer-data/recipes/:id',
      name: 'settings-developer-data-detail-recipes',
      component: () => import('@/views/developer-data/DeveloperRecipesDetailPage.vue'),
      meta: { developer: true },
    },
    {
      path: 'developer-data/goals-history/:id',
      name: 'settings-developer-data-detail-goals-history',
      component: () => import('@/views/developer-data/DeveloperGoalsHistoryDetailPage.vue'),
      meta: { developer: true },
    },
    {
      path: 'developer-data/weight-history/:id',
      name: 'settings-developer-data-detail-weight-history',
      component: () => import('@/views/developer-data/DeveloperWeightHistoryDetailPage.vue'),
      meta: { developer: true },
    },
    {
      path: 'developer-data/settings/:id',
      name: 'settings-developer-data-detail-settings',
      component: () => import('@/views/developer-data/DeveloperSettingsDetailPage.vue'),
      meta: { developer: true },
    },
    {
      path: 'developer-data/meals/:id',
      name: 'settings-developer-data-detail-meals',
      component: () => import('@/views/developer-data/DeveloperMealsDetailPage.vue'),
      meta: { developer: true },
    },
    {
      path: 'developer-data/foods-search-docs/:id',
      name: 'settings-developer-data-detail-foods-search-docs',
      component: () => import('@/views/developer-data/DeveloperFoodsSearchDocsDetailPage.vue'),
      meta: { developer: true },
    },
    {
      path: 'developer-data/sync-configuration/:id',
      name: 'settings-developer-data-detail-sync-configuration',
      component: () => import('@/views/developer-data/DeveloperSyncConfigurationDetailPage.vue'),
      meta: { developer: true },
    },
    {
      path: 'developer-data/auth-session/:id',
      name: 'settings-developer-data-detail-auth-session',
      component: () => import('@/views/developer-data/DeveloperAuthSessionDetailPage.vue'),
      meta: { developer: true },
    },
    {
      path: 'developer-data/sync-records/:id',
      name: 'settings-developer-data-detail-sync-records',
      component: () => import('@/views/developer-data/DeveloperSyncRecordsDetailPage.vue'),
      meta: { developer: true },
    },
    {
      path: 'developer-data/daily-nutrition-totals/:id',
      name: 'settings-developer-data-detail-daily-nutrition-totals',
      component: () => import('@/views/developer-data/DeveloperDailyNutritionTotalsDetailPage.vue'),
      meta: { developer: true },
    },
    {
      path: 'developer-data/:namespace/:id',
      name: 'settings-developer-data-detail',
      redirect: (to) => {
        const routeName = resolveDeveloperDetailRouteName(to.params.namespace);
        if (!routeName || typeof to.params.id !== "string") {
          return {
            name: "settings-developer-data",
            params: { namespace: to.params.namespace },
            query: to.query,
          };
        }
        return {
          name: routeName,
          params: { id: to.params.id },
          query: to.query,
        };
      },
      meta: { developer: true },
    },
    {
      path: 'developer-data/:namespace?',
      name: 'settings-developer-data',
      component: () => import('@/views/DeveloperDataPage.vue'),
      meta: { developer: true },
    },
  ],
};
